import * as React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  CssBaseline,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Typography,
  alpha,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  Download as DownloadIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import AppNavbar from '../../../../components/AppNavbar';
import Header from '../../../../components/Header';
import SideMenu from '../../../../components/SideMenu';
import AppTheme from '../../../../shared-theme/AppTheme';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../../../dashboard/theme/customizations';
import Loans from "./loans";
import Mora from "./mora";
import AssignPrestamoDialog from "./asignprestamodialog";
import EditPortfolioDialog from "../editportafoliodialog";
import { useParams, useNavigate } from "react-router-dom";
import { formatNumberWithCommas } from "../../../../utils/generalFunctions";
const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

// --- Tiny SVG Area Chart (mock) ---
function AreaChart({ data }) {
  // Generate chart data based on portfolio statistics
  const generateChartData = () => {
    if (!data) return [];
    
    const { estadisticas, mora_aging } = data;
    const totalAmount = estadisticas?.monto_total || 0;
    const activeAmount = estadisticas?.monto_activo || 0;
    const delinquentAmount = mora_aging?.total_delinquent_amount || 0;
    const currentAmount = mora_aging?.current_amount || 0;
    
    // Create 12 data points representing monthly performance
    const months = [];
    const currentDate = new Date();
    
    for (let i = 11; i >= 0; i--) {
      const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthName = monthDate.toLocaleDateString('es-ES', { month: 'short' });
      
      // Simulate monthly data based on current portfolio state
      // You can replace this with actual historical data when available
      const baseAmount = totalAmount * 0.8; // Start at 80% of current total
      const growthFactor = 1 + (i * 0.02); // 2% monthly growth
      const monthlyAmount = Math.round(baseAmount * growthFactor);
      
      months.push({
        month: monthName,
        amount: monthlyAmount,
        percentage: (monthlyAmount / totalAmount) * 100
      });
    }
    
    return months;
  };
  
  const chartData = generateChartData();
  const maxAmount = Math.max(...chartData.map(d => d.amount));
  const minAmount = Math.min(...chartData.map(d => d.amount));
  const range = maxAmount - minAmount;
  
  // Generate SVG path
  const generatePath = () => {
    if (chartData.length === 0) return '';
    
    const width = 472;
    const height = 256;
    const padding = 20;
    const chartWidth = width - (padding * 2);
    const chartHeight = height - (padding * 2);
    
    const points = chartData.map((point, index) => {
      const x = padding + (index / (chartData.length - 1)) * chartWidth;
      const normalizedAmount = range > 0 ? (point.amount - minAmount) / range : 0.5;
      const y = height - padding - (normalizedAmount * chartHeight);
      return `${x},${y}`;
    });
    
    const pathData = points.join(' ');
    return `M${pathData}`;
  };
  
  const path = generatePath();
  
  return (
    <Box sx={{ height: 256 }}>
      <svg width="100%" height="100%" viewBox="0 0 472 256" preserveAspectRatio="none">
        <defs>
          <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1976d2" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#1976d2" stopOpacity="0" />
          </linearGradient>
        </defs>
        {path && (
          <>
            <path
              d={`${path} L${472 - 20},256 L20,256 Z`}
              fill="url(#areaFill)"
            />
            <path
              d={path}
              stroke="#1976d2"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
            />
          </>
        )}
      </svg>
    </Box>
  );
}

// --- Donut Chart (SVG strokes) ---
function DonutChart({ data }) {
  const total = data.reduce((a, b) => a + b.value, 0);
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <Box sx={{ position: "relative", width: 160, height: 160 }}>
      <svg viewBox="0 0 40 40" style={{ transform: "rotate(-90deg)" }}>
        {data.map((seg, idx) => {
          const length = (seg.value / total) * circumference;
          const dasharray = `${length} ${circumference - length}`;
          const circle = (
            <circle
              key={idx}
              r={radius}
              cx="20"
              cy="20"
              fill="transparent"
              stroke={seg.color}
              strokeWidth="6"
              strokeDasharray={dasharray}
              strokeDashoffset={-offset}
            />
          );
          offset += length;
          return circle;
        })}
      </svg>
      <Stack sx={{ position: "absolute", inset: 0 }} alignItems="center" justifyContent="center" spacing={0}>
        <Typography variant="h5" fontWeight={700}>{total}</Typography>
        <Typography variant="caption" color="text.secondary">Total Loans</Typography>
      </Stack>
    </Box>
  );
}

function KeyValue({ label, value }) {
  return (
    <Stack direction="row" justifyContent="spaceBetween" sx={{ py: 1.25 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600} ml={1}>{value}</Typography>
    </Stack>
  );
}

function KPICard({ label, value, color }) {
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5" sx={{ mt: 0.5, color: color ? color : undefined }}>{value}</Typography>
      </CardContent>
    </Card>
  );
}

export default function PortfolioDetailsPage(props) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = React.useState(0);
  const [menuEl, setMenuEl] = React.useState(null);
  const [openAssignPrestamoDialog, setOpenAssignPrestamoDialog] = React.useState(false);
  const [openEditDialog, setOpenEditDialog] = React.useState(false);
  // State for portfolio data
  const [portfolioData, setPortfolioData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  
  // Fetch portfolio details
  const fetchPortfolioDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/portafolios/detalle/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al cargar los datos del portafolio');
      }
      
      const data = await response.json();
      setPortfolioData(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching portfolio details:', err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (id) {
      fetchPortfolioDetails();
    }
  }, [id]);

  // Handle loan assignment success
  const handleLoanAssignmentSuccess = () => {
    // Refresh portfolio data after successful loan assignment
    fetchPortfolioDetails();
  };

  // Loading state
  if (loading) {
    return (
      <AppTheme {...props} themeComponents={xThemeComponents}>
        <CssBaseline enableColorScheme />
        <Box sx={{ display: 'flex' }}>
          <SideMenu />
          <AppNavbar />
          <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
            <Typography>Cargando portafolio...</Typography>
          </Box>
        </Box>
      </AppTheme>
    );
  }

  // Error state
  if (error) {
    return (
      <AppTheme {...props} themeComponents={xThemeComponents}>
        <CssBaseline enableColorScheme />
        <Box sx={{ display: 'flex' }}>
          <SideMenu />
          <AppNavbar />
          <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
            <Typography color="error">Error: {error}</Typography>
          </Box>
        </Box>
      </AppTheme>
    );
  }

  // Extract data from API response
  const portfolio = portfolioData?.portafolio || {};
  const prestamos = portfolioData?.prestamos || [];
  const estadisticas = portfolioData?.estadisticas || {};
  const distribucion_producto_financiero = portfolioData?.distribucion_producto_financiero || [];
  // Calculate KPIs from real data
  const kpis = [
    { 
      label: "Balance Total", 
      value: `$${estadisticas.monto_total ? formatNumberWithCommas(estadisticas.monto_total) : '0'}` 
    },
    { 
      label: "Número de Préstamos", 
      value: estadisticas.total_prestamos ? estadisticas.total_prestamos.toString() : '0' 
    },
    { 
      label: "Promedio de Préstamo", 
      value: estadisticas.total_prestamos && estadisticas.monto_total 
        ? `$${Math.round(estadisticas.monto_total / estadisticas.total_prestamos).toLocaleString()}` 
        : '$0' 
    },
    { 
      label: "Balance en Mora", 
      value: portfolioData.mora_aging?.total_delinquent_amount ? `$${portfolioData.mora_aging.total_delinquent_amount.toLocaleString()}` : '$0', 
      color: "error.main" 
    },
    {
      label: "Tasa de Mora",
      value: (() => {
        const capitalEnMora = parseFloat(portfolioData.mora_aging?.total_delinquent_amount) || 0;
        const capitalAlDia = parseFloat(portfolioData.mora_aging?.current_amount) || 0;
        const carteraPendiente = capitalEnMora + capitalAlDia;
        return carteraPendiente > 0 ? `${((capitalEnMora / carteraPendiente) * 100).toFixed(1)}%` : '0%';
      })(),
      color: "error.main"
    },
  ];

  // Calculate donut chart data from real loans
  const loanStatusCounts = prestamos.reduce((acc, loan) => {
    const status = loan.estado || 'activo';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const donutData = [
    { label: "Activo", value: loanStatusCounts.activo || 0, color: "#28a745" },
    { label: "Cancelado", value: loanStatusCounts.cancelado || 0, color: "#dc3545" },
    { label: "Otros", value: (loanStatusCounts.otros || 0) + (loanStatusCounts.pendiente || 0), color: "#ffc107" },
  ].filter(item => item.value > 0);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        {/* Main content */}
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'auto',
          })}
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: 'center',
              mx: 3,
              pb: 5,
              mt: { xs: 8, md: 0 },
            }}
          >
            <Header parent={"Gestión Comercial"} child={"Detalle de Portafolio"} />
            <Container maxWidth={false} sx={{ px: 5, py: 5 }}>
              <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', fontFamily: 'Inter, "Noto Sans", sans-serif' }}>
                <Box maxWidth="xl" mx="auto" py={4}>
                  {/* Title & Actions */}
                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={2} sx={{ mb: 3 }}>
                    <Box>     
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Button startIcon={<ArrowBackIcon />} variant="outlined" size="small" onClick={()=>{navigate(-1)}}>Volver</Button>
                        <Box display="flex" flexDirection="column" >
                          <Typography variant="h4" fontWeight={800}>Portafolio: {portfolio.nombre || 'Cargando...'}</Typography>
                          <Typography variant="body2" color="text.secondary">ID del Portafolio: {portfolio.portafolio_id || 'N/A'}</Typography>
                        </Box>

                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button variant="outlined" startIcon={<EditIcon />} onClick={()=>setOpenEditDialog(true)}>Editar</Button>
                      <Button variant="outlined" startIcon={<PersonAddIcon />} onClick={()=>setOpenAssignPrestamoDialog(true)}>Asignar préstamos</Button>
                      {/*<Button variant="outlined" startIcon={<DownloadIcon />}>Exportar</Button>*/}
                      {/*<IconButton onClick={(e) => setMenuEl(e.currentTarget)}><MoreVertIcon /></IconButton>*/}
                      <Menu open={Boolean(menuEl)} anchorEl={menuEl} onClose={() => setMenuEl(null)}>
                        <MenuItem>Cerrar portafolio</MenuItem>
                        <MenuItem>Reasignar oficial</MenuItem>
                        <MenuItem sx={{ color: "error.main" }}>Eliminar</MenuItem>
                      </Menu>
                    </Stack>
                  </Stack>

                  {/* Chips */}
                  <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                    <Chip 
                      label={portfolio.active ? "Activo" : "Inactivo"} 
                      color={portfolio.active ? "success" : "default"} 
                      variant="outlined" 
                    />
                    <Chip 
                      label={portfolio.descripcion || "Sin descripción"} 
                      color="info" 
                      variant="outlined" 
                    />
                  </Stack>

                  {/* KPI Cards */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {kpis.map((k) => (
                      <Grid key={k.label} item size={2.4}>
                        <KPICard label={k.label} value={k.value} color={k.color} />
                      </Grid>
                    ))}
                  </Grid>

                  {/* Tabs */}
                  <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile >
                      <Tab label="Resumen" sx={{fontSize:17}}/>
                      <Tab label="Préstamos" sx={{fontSize:17,ml:2}}/>
                      <Tab label="Mora" sx={{fontSize:17,ml:2}}/>
                    </Tabs>
                  </Box>

                  {tab === 0 && (
                    <Grid container spacing={3}>
                      <Grid item size={8}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="h6" gutterBottom>Rendimiento (Últimos 12 Meses)</Typography>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Box>
                                <Typography variant="h4" fontWeight={800}>
                                  ${estadisticas.monto_total ? formatNumberWithCommas(estadisticas.monto_total) : '0'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">Balance Total</Typography>
                              </Box>
                              <Box textAlign="right">
                                <Typography variant="h4" fontWeight={800} color="success.main">
                                  ${estadisticas.monto_activo ? formatNumberWithCommas(estadisticas.monto_activo) : '0'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">Balance Activo</Typography>
                              </Box>
                            </Stack>
                            <Box sx={{ mt: 2 }}>
                              <AreaChart data={portfolioData} />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item size={4}>
                        <Card variant="outlined" sx={{ mb: 3 }}>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>Riesgo y Composición</Typography>

                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Préstamos por Estado</Typography>
                            <Stack direction="row" spacing={3} alignItems="center">
                              <DonutChart data={donutData} />
                              <Stack spacing={1} sx={{ flex: 1 }}>
                                {donutData.map((item) => (
                                  <Stack key={item.label} direction="row" justifyContent="space-between" alignItems="center">
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <Box sx={{ width: 10, height: 10, bgcolor: item.color, borderRadius: 1 }} />
                                      <Typography variant="body2">{item.label}</Typography>
                                    </Stack>
                                    <Typography variant="body2">
                                      {item.value} ({estadisticas.total_prestamos ? Math.round((item.value / estadisticas.total_prestamos) * 100) : 0}%)
                                    </Typography>
                                  </Stack>
                                ))}
                              </Stack>
                            </Stack>

                            <Box sx={{ mt: 3 }}>
                              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Distribución por Producto</Typography>
                              <Stack spacing={1.25}>
                                {distribucion_producto_financiero.map((item) => (
                                  <Stack key={item.label} direction="row" spacing={1} alignItems="center">
                                    <Typography variant="caption" color="text.secondary" sx={{ width: 72 }}>{item.producto_financiero}</Typography>
                                    <Box sx={{ flex: 1, height: 8, bgcolor: "#e5e7eb", borderRadius: 9999 }}>
                                      <Box sx={{ width: `${(item.total_prestamos)/100}%`, height: 8, bgcolor: "#1976d2", borderRadius: 9999 }} />
                                    </Box>
                                  </Stack>
                                ))}
                              </Stack>
                            </Box>
                          </CardContent>
                        </Card>

                        <Card variant="outlined">
                          <CardContent>
                            <Stack divider={<Divider />} spacing={0.5}>
                              <KeyValue 
                                label="Oficial(es) Asignado(s)" 
                                value={portfolio.oficiales_asignados || "No asignados"} 
                              />
                              <KeyValue 
                                label="Sucursal(es)" 
                                value={portfolio.sucursales ? portfolio.sucursal_nombre : "No asignadas"} 
                              />
                              <KeyValue 
                                label="Creado el" 
                                value={portfolio.created_at ? new Date(portfolio.created_at).toLocaleDateString('es-ES') : "N/A"} 
                              />
                              <KeyValue 
                                label="Creado por" 
                                value={portfolio.usuario_nombre || "N/A"} 
                              />
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  )}

                  {tab === 1 && (
                    <Loans loans={prestamos} />
                  )}
                  {tab === 2 && (
                    <Mora moraData={portfolioData} />
                  )}
                </Box>
              </Box>
            </Container>
          </Stack>
          <AssignPrestamoDialog
            open={openAssignPrestamoDialog}
            onClose={()=>setOpenAssignPrestamoDialog(false)}
            portafolio_id={id}
            onSuccess={handleLoanAssignmentSuccess}
          />
          <EditPortfolioDialog
            open={openEditDialog}
            onClose={()=>setOpenEditDialog(false)}
            portfolio={portfolio}
          />
        </Box>
      </Box>
    </AppTheme>
  );
}
